import { GUEST_USER_ID, canReceiveFriendRequest } from './legacyMockUsers';
import {
  FEE_RATE_FREE,
  FEE_RATE_PLUS,
  getEstimatedFeeRate,
  hasLockedInPlus,
} from './platformFee';
import {
  FREE_USER_FEE_COPY,
  GUEST_REGISTRATION_BODY,
  GUEST_REGISTRATION_TITLE,
  PLUS_ACTIVE_FEE_COPY,
  PLUS_UPSELL_COPY,
} from '../constants/productCopy';

export {
  GUEST_REGISTRATION_TITLE,
  GUEST_REGISTRATION_BODY,
  FREE_USER_FEE_COPY,
  PLUS_UPSELL_COPY,
  PLUS_ACTIVE_FEE_COPY,
};

/**
 * @typedef {{ firebaseUid?: string|null, userId?: string|null, useCloudFriends?: boolean }} AccessContext
 */

/** Build a consistent access context from session props. */
export function buildAccessContext({
  firebaseUid = null,
  userId = null,
  useCloudFriends = true,
} = {}) {
  return { firebaseUid, userId, useCloudFriends };
}

/** Guest = browse only — no Firebase auth or non-permanent demo session. */
export function isGuestUser(ctx = {}) {
  const { firebaseUid, userId, useCloudFriends = true } = ctx;
  if (!firebaseUid) return true;
  if (!useCloudFriends) return true;
  const effectiveId = userId || firebaseUid;
  return !effectiveId || effectiveId === GUEST_USER_ID || !canReceiveFriendRequest(effectiveId);
}

export function isRegisteredUser(ctx = {}) {
  return !isGuestUser(ctx);
}

export function isPlusSubscriber(profile) {
  return hasLockedInPlus(profile);
}

export function canUseWallet(ctx) {
  return isRegisteredUser(ctx);
}

export function canDeposit(ctx) {
  return isRegisteredUser(ctx);
}

export function canWithdraw(ctx) {
  return isRegisteredUser(ctx);
}

export function canCreateStandardChallenge(ctx) {
  return isRegisteredUser(ctx);
}

export function canAcceptChallenge(ctx) {
  return isRegisteredUser(ctx);
}

export function canCounterChallenge(ctx) {
  return isRegisteredUser(ctx);
}

export function canClaimWinnings(ctx) {
  return isRegisteredUser(ctx);
}

export function canUseCustomChallenges(ctx, profile) {
  return isRegisteredUser(ctx) && hasLockedInPlus(profile);
}

export function canUsePrivateChat(ctx, profile) {
  return isRegisteredUser(ctx) && hasLockedInPlus(profile);
}

export function canStartStripeCheckout(ctx) {
  return isRegisteredUser(ctx);
}

/** Client display estimate only — server decides fee at settlement. */
export function getPlatformFeeRate(profile) {
  return getEstimatedFeeRate(profile);
}

export { FEE_RATE_FREE, FEE_RATE_PLUS };
